import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import {
  HorizontalStackedBarChartCard,
  ChartFilterBar,
  HeatmapChartCard,
} from "../../../components/Charts";
import type { FilterDefinition, FilterValues } from "../../../types/chartFilters";
import type { FilterOnChange } from "../../../components/Charts/ChartFilterBar";
import { CHART_SEMANTIC_CORRECT, CHART_SEMANTIC_INCORRECT } from "../../../constants/chartColors";
import { useP5Data, type P5FilterValues } from "./useP5ChartData";
import CompareButton from "../common/CompareButton";
import styles from "./P5ChartSection.module.css";
import P5OfertaComarcalTableCard from "./P5OfertaComarcalTableCard";

const DEFAULT_FILTERS: P5FilterValues = {
  comarques: undefined,
  sector:  undefined,
};

const IAUP_REFERENCE = 1;

const clamp = (value: number) => Math.min(1, Math.max(0, value));

const mixColor = (from: [number, number, number], to: [number, number, number], ratio: number) => {
  const [fromR, fromG, fromB] = from;
  const [toR, toG, toB] = to;

  return `rgb(${Math.round(fromR + (toR - fromR) * ratio)}, ${Math.round(
    fromG + (toG - fromG) * ratio
  )}, ${Math.round(fromB + (toB - fromB) * ratio)})`;
};

const formatSignedDelta = (value: number) => {
  const rounded = Number(value.toFixed(2));
  const formatted = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1).replace(/\.0$/, "");

  return `${rounded > 0 ? "+" : ""}${formatted}`;
};

const formatPrimaryValue = (value: number) => {
  const rounded = Number(value.toFixed(2));
  return Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1).replace(/\.0$/, "");
};

const getP5HeatmapCellColor = (value: number) => {
  /** Sense places / sense IAUP vàlid: no és alineació negativa, es mostra neutre en gris */
  if (value === 0) {
    return {
      backgroundColor: "#f4f4f5",
      color: "#52525b",
    };
  }

  const delta = value - IAUP_REFERENCE;
  const abs = Math.abs(delta);
  const intensity = clamp(abs / 1.2);

  if (abs <= 0.04) {
    return {
      backgroundColor: "#DCFCE7",
      color: CHART_SEMANTIC_CORRECT,
    };
  }

  if (abs <= 0.25) {
    return {
      backgroundColor: mixColor([255, 251, 235], [245, 158, 11], intensity * 0.85),
      color: intensity > 0.45 ? "#FFFFFF" : "#92400E",
    };
  }

  const r = 220;
  const g = 38;
  const b = 38;
  return {
    backgroundColor: mixColor([254, 242, 242], [r, g, b], intensity),
    color: intensity > 0.45 ? "#FFFFFF" : CHART_SEMANTIC_INCORRECT,
  };
};

const P5ChartSection = () => {
  const [filters, setFilters] = useState<P5FilterValues>(DEFAULT_FILTERS);
  const {
    heatmapData,
    heatmapSeries,
    heatmapRowMeta,
    heatmapColumnMeta,
    heatmapSummary,
    chart2Data,
    chart2Bars,
    chart2Mean,
    chart2Domain,
    availableComarques,
    availableSectors,
    defaultComarques,
    ofertaComarcalRows,
    selectedSector,
    getIaupLevelColor,
    loading,
    error,
  } = useP5Data(filters);

  const filterDefs: FilterDefinition[] = useMemo(() => [
    {
      type: "multiSelect" as const,
      key: "comarques",
      label: "Comarques",
      placeholder: "Comarques a comparar",
      options: availableComarques.map((c) => ({ value: c, label: c })),
    },
  ], [availableComarques]);

  const handleFilterChange: FilterOnChange = (key, value) => {
    setFilters((prev) => {
      if (key === "comarques") {
        return { ...prev, comarques: (value as string[] | undefined) ?? [] };
      }
      return { ...prev, [key]: value || undefined };
    });
  };

  useEffect(() => {
    if (filters.comarques === undefined && defaultComarques.length > 0) {
      setFilters((prev) => ({ ...prev, comarques: defaultComarques }));
    }
  }, [defaultComarques, filters.comarques]);

  const noComarquesSelected = Array.isArray(filters.comarques) && filters.comarques.length === 0;

  return (
    <>
      <ChartFilterBar
        filters={filterDefs}
        values={filters as unknown as FilterValues}
        onChange={handleFilterChange}
        disabled={loading}
      />
      {noComarquesSelected ? (
        <div className={styles.warningNotice}>⚠ Cal seleccionar almenys una comarca.</div>
      ) : null}
      {!noComarquesSelected ? (
        <>
      {/*
      Primer gràfic (SectorAlignmentLineChartCard) comentat temporalment: reactivar amb
      chart1Data, chart1Lines, lineChartDomain des de useP5Data i import SectorAlignmentLineChartCard.
      */}
      {!loading && !error && heatmapSummary ? (
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Sector més desajustat</div>
            <div className={styles.summaryValue}>{heatmapSummary.topSector.label}</div>
            <div className={styles.summaryHint}>
              IAUP mitja {heatmapSummary.topSector.avgIaup} · Δ {formatSignedDelta(heatmapSummary.topSector.avgDelta)}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Comarca més desajustada</div>
            <div className={styles.summaryValue}>{heatmapSummary.topComarca.label}</div>
            <div className={styles.summaryHint}>
              IAUP mitja {heatmapSummary.topComarca.avgIaup} · Δ {formatSignedDelta(heatmapSummary.topComarca.avgDelta)}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Pic de desajust</div>
            <div className={styles.summaryValue}>
              {heatmapSummary.topCell.sector} · {heatmapSummary.topCell.comarca}
            </div>
            <div className={styles.summaryHint}>
              IAUP {heatmapSummary.topCell.value} · Δ {formatSignedDelta(heatmapSummary.topCell.delta)}
            </div>
          </div>
        </div>
      ) : null}
      <HeatmapChartCard
        title="Mapa de calor sectorial per comarca"
        subtitle="Ordenat per criticitat mitjana. Cada cel·la mostra el valor IAUP i la desviació respecte al punt d'equilibri (= 1)."
        data={heatmapData}
        xKey="nom_comarca"
        series={heatmapSeries}
        rowAxisLabel="Sector"
        loading={loading}
        error={error}
        valueSuffix=""
        valueColorScale={(value) => getP5HeatmapCellColor(value)}
        legendLabels={{
          low: "Infraalineació · IAUP < 1",
          mid: "Equilibri · IAUP = 1",
          high: "Sobrealineació · IAUP > 1",
        }}
        legendScaleStyle="linear-gradient(90deg, #FECACA 0%, #FEF9C3 28%, #DCFCE7 50%, #FEF9C3 72%, #FECACA 100%)"
        rowMeta={heatmapRowMeta}
        columnMeta={heatmapColumnMeta}
        formatCellValue={(value) => ({
          primary: formatPrimaryValue(value),
          secondary: `Δ ${formatSignedDelta(value - IAUP_REFERENCE)}`,
        })}
      />
      <HorizontalStackedBarChartCard
        title={`IAUP per comarca en el sector ${selectedSector ?? ""}`}
        data={chart2Data}
        yKey="nom_comarca"
        bars={chart2Bars}
        loading={loading}
        error={error}
        xDomain={chart2Domain}
        tooltipSuffix=""
        stacked={false}
        labelMinThreshold={0}
        minPointSize={3}
        referenceValue={chart2Mean}
        referenceLabel="Mitjana sectorial"
        getBarColor={(value) => getIaupLevelColor(value)}
        headerExtra={
          <Select
            value={selectedSector}
            options={availableSectors.map((s) => ({ value: s, label: s }))}
            placeholder="Sector"
            onChange={(value) => handleFilterChange("sector", value)}
            style={{ width: 220 }}
            disabled={loading}
          />
        }
      />
      <P5OfertaComarcalTableCard data={ofertaComarcalRows} />
      <CompareButton />
        </>
      ) : null}
    </>
  );
};

export default P5ChartSection;
